import { useEffect, useRef, useState } from "react";
import { CaretDown, X } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export const ROSTER_STATUS_META = {
  active: { label: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  leave: { label: "On leave", cls: "bg-[#B7791F]/10 text-[#B7791F] border-[#B7791F]/20" },
  inactive: { label: "Inactive", cls: "bg-stone-100 text-stone-600 border-stone-200" },
  resigned: { label: "Resigned", cls: "bg-stone-100 text-stone-600 border-stone-200" },
  terminated: { label: "Terminated", cls: "bg-[#C0392B]/10 text-[#C0392B] border-[#C0392B]/20" },
  never_worked: { label: "Never worked", cls: "bg-stone-100 text-stone-500 border-dashed border-stone-300" },
  declined: { label: "Declined", cls: "bg-stone-100 text-stone-500 border-stone-200" },
  applicant: { label: "Applicant", cls: "bg-[#4A235A]/10 text-[#4A235A] border-[#4A235A]/20" },
};

// Owner 2026-09: this roster is EMPLOYEES — declined/applicant live in the hiring pipeline, not here.
// "Never worked" is its own status: hired or onboarded but never worked a day — distinct from
// someone who worked and was later terminated.
export const ASSIGNABLE_STATUSES = ["active", "leave", "inactive", "resigned", "terminated", "never_worked"];

const REASONS = {
  terminated: ["Attendance / call-outs", "Performance", "Policy violation", "Compliance lapse", "Job abandonment / no contact", "Other"],
  resigned: ["New job", "Personal / family", "Schedule", "Pay", "Moved away", "No call, no show", "Other"],
  leave: ["Medical", "Family / caregiving", "Maternity / paternity", "Personal", "Other"],
  inactive: ["Not currently scheduled", "Availability ended", "Other"],
  never_worked: ["Never started / no-show to onboarding", "Withdrew before first shift", "Could not complete requirements", "Other"],
  active: ["Returned to work", "Reactivated", "Correction", "Other"],
};

function StatusForm({ uid, target, current, onDone, onCancel }) {
  const isSep = target === "terminated" || target === "resigned";
  const [effective, setEffective] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [character, setCharacter] = useState("");
  const [rehire, setRehire] = useState(false);
  const [expectedReturn, setExpectedReturn] = useState("");
  const [sendLetter, setSendLetter] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ctx, setCtx] = useState(null);

  useEffect(() => {
    api.get(`/users/${uid}/separation-context`).then((r) => setCtx(r.data)).catch(() => setCtx(false));
  }, [uid]);

  const submit = async () => {
    if (!effective) { toast.error("Pick the effective date"); return; }
    if (isSep && !reason) { toast.error("A separation needs a reason"); return; }
    setBusy(true);
    try {
      await api.put(`/users/${uid}/status`, {
        status: target, effective_date: effective, reason, notes,
        character_notes: character, rehire_eligible: rehire, expected_return: expectedReturn,
        send_letter: sendLetter,
      });
      toast.success(`${ROSTER_STATUS_META[target].label} — saved with effective date ${effective}`);
      onDone(target);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="status-form-modal">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-heading text-xl tracking-tight text-[#4A235A]">
              {isSep ? (target === "terminated" ? "Termination form" : "Resignation form") : `Status change — ${ROSTER_STATUS_META[target].label}`}
            </p>
            <p className="mt-0.5 text-xs text-stone-500">
              {isSep
                ? "Filed to their employee record. The what and whys matter — they reveal character more than anything."
                : "Every status change is filed with a date and a reason."}
            </p>
          </div>
          <button onClick={onCancel} className="text-stone-400 hover:text-stone-700" data-testid="status-form-close"><X size={18} /></button>
        </div>

        <div className="mt-4 space-y-3 text-left">
          {ctx && ctx !== false && (
            <p className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-600" data-testid="status-form-dates">
              Started <strong>{ctx.start_date || "—"}</strong> · first shift <strong>{ctx.first_worked || "never"}</strong> · last worked <strong>{ctx.last_worked || "never"}</strong>
              {!ctx.first_worked && !ctx.last_worked && <span className="ml-1 font-semibold text-stone-500">— never worked a day</span>}
            </p>
          )}
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-stone-400">Effective date *</span>
            <input type="date" value={effective} onChange={(e) => setEffective(e.target.value)}
              className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="status-form-date" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-stone-400">Reason{isSep ? " *" : ""}</span>
            <select value={reason} onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm" data-testid="status-form-reason">
              <option value="">Choose…</option>
              {(REASONS[target] || REASONS.active).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          {isSep && (
            <div className="rounded-lg border border-stone-200 bg-stone-50 p-3" data-testid="status-form-context">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">On their file — document from this</p>
              {!ctx && <p className="mt-1 text-xs text-stone-400">Pulling their history…</p>}
              {ctx === false && <p className="mt-1 text-xs text-stone-400">Couldn't load their history.</p>}
              {ctx && ctx !== false && (
                <div className="mt-1 space-y-1.5">
                  <p className="text-xs font-medium text-stone-700" data-testid="status-form-ctx-summary">
                    {ctx.visits} visits on record · {ctx.missed_punches.total} missed punches ({ctx.missed_punches.not_their_fault} not their fault) · {ctx.compliance_outstanding} open compliance item{ctx.compliance_outstanding === 1 ? "" : "s"}
                  </p>
                  {(ctx.records || []).slice(-3).reverse().map((r, i) => (
                    <p key={`r${i}`} className="text-[11px] text-stone-500">
                      <span className="font-medium text-stone-600">{String(r.at || "").slice(0, 10)}</span> — {r.title} {r.by ? <span className="text-stone-400">({r.by})</span> : null}
                    </p>
                  ))}
                  {(ctx.notes || []).slice(-3).reverse().map((n, i) => (
                    <p key={`n${i}`} className="text-[11px] italic text-stone-500">
                      <span className="font-medium not-italic text-stone-600">{String(n.at || "").slice(0, 10)}</span> — "{n.text}"
                    </p>
                  ))}
                  {!(ctx.records || []).length && !(ctx.notes || []).length && (
                    <p className="text-[11px] text-stone-400">No prior notes or records on file.</p>
                  )}
                </div>
              )}
            </div>
          )}
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-stone-400">What happened</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              placeholder={isSep ? "The story, in your own words — what led here…" : "Details worth remembering…"}
              className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="status-form-notes" />
          </label>
          {isSep && (
            <>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-stone-400">What did this reveal about them?</span>
                <textarea value={character} onChange={(e) => setCharacter(e.target.value)} rows={2}
                  placeholder="How they handled it — accountability, blame, honesty, silence…"
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="status-form-character" />
              </label>
              <label className="flex items-center gap-2 text-sm text-stone-700" data-testid="status-form-rehire-row">
                <input type="checkbox" checked={rehire} onChange={(e) => setRehire(e.target.checked)} className="accent-[#4A235A]" data-testid="status-form-rehire" />
                Eligible for rehire
              </label>
              <label className="flex items-center gap-2 text-sm text-stone-700" data-testid="status-form-letter-row">
                <input type="checkbox" checked={sendLetter} onChange={(e) => setSendLetter(e.target.checked)} className="accent-[#4A235A]" data-testid="status-form-letter" />
                Prepare the {target === "resigned" ? "resignation confirmation" : "termination"} letter (PDF on their file — the office reviews &amp; sends; nothing auto-sends)
              </label>
            </>
          )}
          {target === "leave" && (
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-stone-400">Expected return (optional)</span>
              <input type="date" value={expectedReturn} onChange={(e) => setExpectedReturn(e.target.value)}
                className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="status-form-return" />
            </label>
          )}
        </div>

        <div className="mt-5 flex gap-2">
          <button onClick={submit} disabled={busy}
            className="flex-1 rounded-full bg-[#4A235A] px-5 py-2 text-sm font-semibold text-white hover:bg-stone-900 disabled:opacity-50"
            data-testid="status-form-save">
            {busy ? "Saving…" : "Save status change"}
          </button>
          <button onClick={onCancel} className="rounded-full border border-stone-300 px-5 py-2 text-sm text-stone-600 hover:bg-stone-50" data-testid="status-form-cancel">Cancel</button>
        </div>
        {isSep && (
          <p className="mt-2 text-center text-[11px] text-stone-400">
            Save without the story and the office gets reminded every day until it's written.
          </p>
        )}
      </div>
    </div>
  );
}

export function StatusPill({ uid, status, onChanged, testid }) {
  const [open, setOpen] = useState(false);
  const [formTarget, setFormTarget] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const meta = ROSTER_STATUS_META[status] || ROSTER_STATUS_META.inactive;

  return (
    <div className="relative inline-block" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-shadow hover:shadow-sm ${meta.cls}`}
        title="Click to change status" data-testid={testid}>
        {meta.label} <CaretDown size={10} />
      </button>
      {open && (
        <div className="absolute left-0 z-30 mt-1 w-36 overflow-hidden rounded-lg border border-stone-200 bg-white py-1 shadow-lg" data-testid={`${testid}-menu`}>
          {ASSIGNABLE_STATUSES.map((k) => {
            const m = ROSTER_STATUS_META[k];
            return (
              <button key={k} type="button" onClick={() => { setOpen(false); if (k !== status) setFormTarget(k); }}
                className={`block w-full px-3 py-1.5 text-left text-xs text-stone-700 hover:bg-stone-50 ${k === status ? "font-bold" : ""}`}
                data-testid={`${testid}-opt-${k}`}>
                {m.label}
              </button>
            );
          })}
        </div>
      )}
      {formTarget && (
        <StatusForm uid={uid} target={formTarget} current={status}
          onDone={(s) => { setFormTarget(""); if (onChanged) onChanged(s); }}
          onCancel={() => setFormTarget("")} />
      )}
    </div>
  );
}
